import { Link } from "wouter";
import { CheckCircle2, Heart, Hand, Leaf } from "lucide-react";
import { CREDENTIALS, BRAND } from "@/lib/data";

// Real photos of Stephanie Mason
const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_wellness-at-home-15/artifacts/hk2fpibb_image1.jpeg";
const SECONDARY =
  "https://customer-assets.emergentagent.com/job_wellness-at-home-15/artifacts/9hwlvvc5_image0.jpeg";

const PILLARS = [
  { icon: Heart, title: "Presence", text: "I treat every hour as a held space — phones away, intentions set, slow exhale." },
  { icon: Hand, title: "Craft", text: "Every technique is deliberate. Pressure is communicated, never guessed at." },
  { icon: Leaf, title: "Care", text: "Heated linens, hand-blended oils, music that fits the moment. Nothing rushed." },
];

export default function About() {
  return (
    <div data-testid="page-about">
      {/* INTRO */}
      <section className="bg-spa-cream">
        <div className="container-spa pt-16 sm:pt-20 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <div className="lg:col-span-7">
              <p className="overline mb-5">About Stephanie</p>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-spa-dark leading-[1.02]">
                Quiet hands.<br />
                <span className="italic text-spa-green">Honest care.</span>
              </h1>
              <p className="mt-8 text-lg text-spa-gray leading-relaxed">
                I&apos;m Stephanie — a licensed massage therapist who left the salon model so I could offer
                something rarer: an unrushed, in-home session built around the person on the table.
              </p>
              <p className="mt-5 text-spa-gray leading-relaxed">
                I trained in Knoxville, completed over 650 clinical hours, and have spent the last several
                years building a quiet practice with regulars who feel like neighbors. My work is gentle
                where it should be, and patient where it needs to be.
              </p>
              <div className="mt-10 flex gap-4">
                <Link href="/services" data-testid="about-cta-book"><button className="btn-primary">Book a session</button></Link>
                <Link href="/products" data-testid="about-cta-products"><button className="btn-secondary">See products</button></Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div className="overflow-hidden rounded-[2rem] aspect-[4/5] shadow-xl">
                  <img src={PORTRAIT} alt="Stephanie Mason, LMT" className="w-full h-full object-cover" />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-spa-green text-spa-cream rounded-2xl px-6 py-5 shadow-lg max-w-[220px]">
                  <p className="font-serif text-xl leading-tight">{BRAND.founder}</p>
                  <p className="text-xs text-spa-cream/70 mt-1">Founder & Therapist</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-spa-sand">
        <div className="container-spa section-padding">
          <div className="max-w-2xl mb-16">
            <p className="overline mb-4">My philosophy</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-spa-dark leading-[1.05]">
              Three things I never compromise on.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {PILLARS.map((p) => (
              <div key={p.title} data-testid={`pillar-${p.title.toLowerCase()}`} className="bg-white rounded-2xl p-8 border border-spa-border">
                <p.icon className="w-7 h-7 text-spa-cyan mb-5" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl text-spa-dark mb-3">{p.title}</h3>
                <p className="text-spa-gray leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CREDENTIALS */}
      <section className="bg-spa-cream">
        <div className="container-spa section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-2xl aspect-[5/6]">
                <img src={SECONDARY} alt="Stephanie at her practice" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="lg:col-span-7">
              <p className="overline mb-4">Credentials</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-spa-dark leading-[1.05] mb-10">
                Trained, licensed, and accountable.
              </h2>
              <ul className="space-y-4">
                {CREDENTIALS.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-spa-dark">
                    <CheckCircle2 className="w-5 h-5 text-spa-green mt-0.5 shrink-0" strokeWidth={1.5} />
                    <span className="font-sans leading-relaxed">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
